import { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  LinearProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import type { BoxConfiguration } from '@/types';

interface BoxConfigurationCardProps {
  configuration: BoxConfiguration;
  index: number;
  onOpen: (id: number) => void;
  onRename?: (id: number, name: string) => void;
  onDelete?: (id: number) => void;
}

const CardRoot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'confirmed',
})<{ confirmed: boolean }>(({ theme, confirmed }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
  padding: theme.spacing(2),
  height: '100%',
  backgroundColor: theme.palette.background.paper,
  border: `${confirmed ? 2 : 1}px solid`,
  borderColor: confirmed ? theme.palette.primary.main : theme.palette.divider,
  borderRadius: theme.shape.borderRadius,
}));

function lastSavedDate(c: BoxConfiguration): string | null {
  return c.lastAnswerDate ?? c.creationDate ?? null;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}

export function BoxConfigurationCard({
  configuration,
  index,
  onOpen,
  onRename,
  onDelete,
}: BoxConfigurationCardProps) {
  const confirmed = configuration.confirmationDate != null;
  const name = configuration.name || `Configurazione box #${configuration.id}`;
  const pct = Math.round(configuration.progress);

  const showActions = onRename != null || onDelete != null;
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [isRenaming, setIsRenaming] = useState(false);
  const [draftName, setDraftName] = useState(name);
  const closeMenu = (): void => setMenuAnchor(null);

  const startRenaming = (): void => {
    setDraftName(configuration.name);
    setIsRenaming(true);
    closeMenu();
  };
  const cancelRenaming = (): void => {
    setIsRenaming(false);
    setDraftName(configuration.name);
  };
  const saveRenaming = (): void => {
    const trimmed = draftName.trim();
    setIsRenaming(false);
    if (trimmed && trimmed !== configuration.name) onRename?.(configuration.id, trimmed);
  };

  return (
    <CardRoot confirmed={confirmed}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.secondary', minWidth: 0 }}>
          <AccessTimeIcon fontSize="small" aria-hidden="true" />
          <Typography variant="caption" noWrap>
            {formatDate(lastSavedDate(configuration))}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          <Chip
            size="small"
            label={confirmed ? 'Confermata' : 'Bozza'}
            color={confirmed ? 'primary' : 'default'}
            variant={confirmed ? 'filled' : 'outlined'}
            {...(confirmed ? { icon: <CheckCircleIcon fontSize="small" /> } : {})}
            sx={{ fontWeight: 500 }}
          />
          {showActions && !confirmed && (
            <IconButton
              size="small"
              aria-label={`Azioni per la configurazione ${name}`}
              onClick={(e) => setMenuAnchor(e.currentTarget)}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
        <Typography variant="h4" component="span" color="primary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {String(index).padStart(2, '0')}.
        </Typography>
        {isRenaming ? (
          <TextField
            autoFocus
            fullWidth
            size="small"
            variant="standard"
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') saveRenaming();
              if (e.key === 'Escape') cancelRenaming();
            }}
            inputProps={{ 'aria-label': 'Nuovo nome configurazione box' }}
          />
        ) : (
          <Typography variant="h4" component="span" sx={{ minWidth: 0 }}>
            {name}
          </Typography>
        )}
      </Box>

      {isRenaming && (
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="contained" color="primary" onClick={saveRenaming}>
            Salva
          </Button>
          <Button size="small" color="inherit" onClick={cancelRenaming}>
            Annulla
          </Button>
        </Stack>
      )}

      <Typography variant="caption" color="text.secondary">
        {configuration.boxCount} box · {configuration.answeredCount}/{configuration.totalCount} scelte
      </Typography>

      <Box sx={{ mt: 'auto' }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
          Completata al {pct}%
        </Typography>
        <LinearProgress
          variant="determinate"
          value={pct}
          sx={{ height: 6, borderRadius: 1, backgroundColor: 'divider' }}
          aria-label={`Avanzamento ${pct} percento`}
        />
      </Box>

      <Button
        variant={confirmed ? 'outlined' : 'contained'}
        color="primary"
        onClick={() => onOpen(configuration.id)}
        sx={{ alignSelf: 'flex-start' }}
        aria-label={`Apri la configurazione ${name}`}
      >
        {confirmed ? 'Visualizza' : 'Continua la personalizzazione'}
      </Button>

      <Menu anchorEl={menuAnchor} open={menuAnchor != null} onClose={closeMenu}>
        {onRename != null && (
          <MenuItem onClick={startRenaming}>
            <ListItemIcon>
              <DriveFileRenameOutlineIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Rinomina</ListItemText>
          </MenuItem>
        )}
        {onDelete != null && (
          <MenuItem
            onClick={() => {
              closeMenu();
              onDelete(configuration.id);
            }}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon>
              <DeleteOutlineIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Elimina</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </CardRoot>
  );
}
